import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Pagination,
  Box,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';
import GroupsIcon from '@mui/icons-material/Groups';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { usePolyAuth } from '../../hooks/PolyAuthContext';
import { formatDate } from '../../utils/dateFormatters';

function PlatformIcon({ platform }) {
  const key = (platform || '').toLowerCase();
  const isZoom = key.includes('zoom');
  const isTeams = key.includes('teams');
  const Icon = isZoom ? VideocamIcon : isTeams ? GroupsIcon : QuestionMarkIcon;
  return (
    <Tooltip title={platform || 'Unknown'} arrow placement="top">
      <Icon sx={{ fontSize: '1rem', color: 'text.secondary', verticalAlign: 'middle' }} />
    </Tooltip>
  );
}

function MeetingHistoryDialog({ open, onClose, polyUserId, participantName, accountName }) {
  const { api_client, accountId } = usePolyAuth();
  const { t } = useTranslation('shared');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Meeting-history dialog: request meetings only so pagination is exact
  // (trust-checks would otherwise occupy slots in the combined stream).
  const swrKey = open && polyUserId && accountId
    ? `/v2/logs/user/${polyUserId}/account/${accountId}?page=${page}&page_size=${pageSize}&log_types=meeting`
    : null;

  const fetcher = async (url) => {
    const response = await api_client.get(url);
    return response.data;
  };

  const { data, isLoading } = useSWR(swrKey, fetcher, {
    revalidateOnFocus: false,
  });

  // The endpoint now returns a unified `logs` array (meetings + trust
  // checks). This dialog is meeting-history only, so filter to meetings.
  const meetings = (data?.logs ?? []).filter((l) => l.callType === 'meeting');
  const totalPages = data?.pagination?.total_pages ?? 0;

  const tableFontSize = '0.72rem';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: { margin: '4px !important', width: 'calc(100% - 8px) !important', maxWidth: 'none !important' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, px: 1.5 }}>
        <Typography variant="subtitle2" component="span" noWrap sx={{ fontSize: '0.85rem' }}>
          {participantName}
          <Typography component="span" color="text.secondary" sx={{ mx: 0.75, fontSize: '0.85rem' }}>
            &harr;
          </Typography>
          {accountName || 'Account'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 1, pb: 1 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : meetings.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, fontSize: tableFontSize }}>
            {t('meetingHistory.noHistory')}
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: tableFontSize, py: 0.5, px: 0.75 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 28, px: '4px !important' }} />
                    <TableCell>{t('meetingHistory.topic')}</TableCell>
                    <TableCell>{t('meetingHistory.when')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {meetings.map((meeting, index) => (
                    <TableRow key={index} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ width: 28, px: '4px !important', textAlign: 'center' }}>
                        <PlatformIcon platform={meeting.meeting?.platform} />
                      </TableCell>
                      <TableCell>
                        <Box>{meeting.meeting?.topic || 'Untitled Meeting'}</Box>
                        {meeting.meeting?.host_name && (
                          <Typography component="span" sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>
                            {meeting.meeting.host_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDate(meeting.meeting?.start_time)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 0.5 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, newPage) => setPage(newPage)}
                  size="small"
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default MeetingHistoryDialog;
